import { apiClient, unwrap } from '@/lib/apiClient';
import type { ApiSuccess, PaginationMeta } from '@/types/api';
import type {
  AssignHousePayload,
  AssignHouseResult,
  House,
  HouseList,
  HousePayload,
  HouseStatus,
  HouseStudent,
  HouseStudentParams,
} from '@/types/house';

/**
 * Every call about the school's houses.
 *
 * No school id in any path: the server takes it from the caller's token, so
 * nothing here can point at another school's houses or students.
 */
export const housesApi = {
  /** Every house with this year's student count, and how many are in none. */
  list: () => unwrap(apiClient.get<ApiSuccess<HouseList>>('/institute/houses')),

  /**
   * This year's students, filtered like the register — a house's members, or
   * the students to choose from when assigning. Blank filters are dropped, as
   * every list call here does.
   */
  listStudents: async (
    params: HouseStudentParams,
  ): Promise<{ items: HouseStudent[]; meta: PaginationMeta }> => {
    const query = Object.fromEntries(
      Object.entries(params).filter(([, value]) => value !== '' && value !== undefined),
    );
    const response = await apiClient.get<ApiSuccess<HouseStudent[]>>('/institute/houses/students', {
      params: query,
    });
    return {
      items: response.data.data,
      meta: response.data.meta ?? {
        page: 1,
        limit: 20,
        total: response.data.data.length,
        totalPages: 1,
      },
    };
  },

  create: (payload: HousePayload) =>
    unwrap(apiClient.post<ApiSuccess<House>>('/institute/houses', payload)),

  update: (uuid: string, payload: HousePayload) =>
    unwrap(apiClient.patch<ApiSuccess<House>>(`/institute/houses/${uuid}`, payload)),

  /** Suspends or reactivates a house. Its members stay in it either way. */
  setStatus: (uuid: string, status: HouseStatus) =>
    unwrap(apiClient.patch<ApiSuccess<House>>(`/institute/houses/${uuid}/status`, { status })),

  /** Deletes a suspended house for good; any students left in it go to "Not assigned". */
  remove: (uuid: string) => unwrap(apiClient.delete<ApiSuccess<null>>(`/institute/houses/${uuid}`)),

  /** One student or many into a house, or out of theirs with `houseUuid: null`. */
  assign: (payload: AssignHousePayload) =>
    unwrap(apiClient.post<ApiSuccess<AssignHouseResult>>('/institute/houses/assign', payload)),
};
